import { MockViolation } from '../types/mockdetector.types';
import { DatabaseViolation } from '../types/database.types';

/**
 * Get severity score for prioritization
 */
export const getSeverityScore = (severity: string): number => {
  switch (severity) {
    case 'critical': return 4;
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 0;
  }
};

/**
 * Calculate priority score (severity × confidence) for smart sorting
 */
export const getPriorityScore = (violation: MockViolation | DatabaseViolation): number => {
  const severityWeight = getSeverityScore(violation.severity);
  const confidenceWeight = (violation.confidence || 50) / 100;
  return severityWeight * confidenceWeight;
};

/**
 * ✅ PHASE 3: Confidence Scoring System
 * Calculates how confident we are that a violation is REAL mock data (not a false positive)
 * Scale: 0-100%
 * - 90-100%: Very confident (definitely mock data)
 * - 70-89%: High confidence (likely mock data)
 * - 50-69%: Medium confidence (possibly mock data)
 * - 30-49%: Low confidence (uncertain)
 * - 0-29%: Very low confidence (likely false positive)
 */

export function calculateViolationConfidence(violation: MockViolation | DatabaseViolation): number {
  let confidence = 50; // Base confidence
  const code = 'code' in violation ? violation.code?.toLowerCase() || '' : '';
  const file = 'file' in violation ? violation.file?.toLowerCase() || '' : '';
  const description = violation.description.toLowerCase();

  // ========= CRITICAL PATTERN BOOSTS (NEW) =========
  
  // 🔴 React useState array (NEW)
  if (description.includes('usestate') && description.includes('array')) {
    confidence = 85; // Very high confidence
  }
  
  // 🔴 useEffect fake data (NEW)
  if (description.includes('useeffect') && (code.includes('fakeresponse') || code.includes('mockdata'))) {
    confidence = 90; // Extremely high confidence
  }
  
  // 🔴 Test array variables (NEW)
  if (code.match(/const\s+(testUsers|mockUsers|fakeData)/)) {
    confidence = 85;
  }

  // ========= HIGH CONFIDENCE INDICATORS (+25 to +40 points) =========
  
  // 🔴 Extremely obvious mock data (+40)
  if (code.includes('test@example.com') || code.includes('user@example.com')) {
    confidence += 40;
  }
  if (code.includes('lorem ipsum') || code.includes('dolor sit amet')) {
    confidence += 40;
  }
  if (code.includes('array.fill(') || code.includes('array(10).fill(')) {
    confidence += 35;
  }
  if (code.includes('555-1234') || code.includes('555-5555')) {
    confidence += 35;
  }
  
  // 🟠 Very likely mock data (+30)
  if (code.match(/const\s+(mock|test|demo|sample)(data|users|items|products)/i)) {
    confidence += 30;
  }
  if (code.match(/test_user|demo_account|sample_item|mock_product/i)) {
    confidence += 30;
  }
  
  // 🟡 Strong mock indicators (+25)
  if (code.match(/\[\s*{\s*id:\s*1\s*,/)) {
    confidence += 25; // Hardcoded array starting with id:1
  }
  if (code.match(/user\s*#?\d+|item\s*#?\d+/i)) {
    confidence += 25; // Sequential names
  }

  // ========= MEDIUM CONFIDENCE INDICATORS (+10 to +20 points) =========
  
  if (code.match(/123\s*main\s*street|456\s*elm\s*st/i)) {
    confidence += 15;
  }
  if (code.includes('acme corp') || code.includes('test company')) {
    confidence += 15;
  }
  if (code.match(/\$9\.99|\$19\.99|\$99\.99/)) {
    confidence += 10;
  }

  // ========= NEGATIVE INDICATORS (Reduce confidence - likely false positive) =========
  
  // 🟢 Legitimate code patterns (-30 to -50)
  if (file.includes('.config.') || file.includes('tailwind.config') || file.includes('vite.config')) {
    confidence -= 50; // Config files are almost never mock data
  }
  if (file.includes('mockdetector') || file.includes('scanpatterns')) {
    confidence -= 50; // MockDetector's own code
  }
  if (code.includes('supabase_publishable_key') || code.includes('vite_supabase')) {
    confidence -= 40; // Supabase keys MUST be in code
  }
  if (code.includes('.from(') && code.includes('.select(')) {
    confidence -= 30; // Database queries are legitimate
  }
  if (code.includes('import') || code.includes('export')) {
    confidence -= 20; // Import/export statements
  }
  
  // 🟢 Configuration and metadata (-25 to -35)
  if (description.includes('configuration') || description.includes('settings')) {
    confidence -= 30;
  }
  if (description.includes('api response') || description.includes('metadata')) {
    confidence -= 35;
  }
  if (description.includes('batch insert') && description.includes('minutes ago')) {
    confidence -= 40; // Recent batch inserts are legitimate
  }

  // ========= CONTEXT-BASED ADJUSTMENTS =========
  
  // React components get +20 boost
  if (file.includes('/components/') || file.includes('/pages/')) {
    confidence += 20;
  }
  
  // Database violations get +10 confidence (more reliable)
  if ('table' in violation) {
    confidence += 10;
  }
  
  // Critical severity gets +5 confidence
  if (violation.severity === 'critical') {
    confidence += 5;
  }

  // Clamp to 0-100 range
  return Math.max(0, Math.min(100, Math.round(confidence)));
}

/**
 * Get confidence level label
 */
export function getConfidenceLevel(confidence: number): {
  level: 'very_high' | 'high' | 'medium' | 'low' | 'very_low';
  label: string;
  color: string;
  emoji: string;
} {
  if (confidence >= 85) {  // Changed from 90
    return { 
      level: 'very_high', 
      label: 'Very High Confidence', 
      color: 'text-red-600', 
      emoji: '🔴' 
    };
  }
  if (confidence >= 70) {
    return { 
      level: 'high', 
      label: 'High Confidence', 
      color: 'text-orange-600', 
      emoji: '🟠' 
    };
  }
  if (confidence >= 55) {  // Changed from 50
    return { 
      level: 'medium', 
      label: 'Medium Confidence', 
      color: 'text-yellow-600', 
      emoji: '🟡' 
    };
  }
  if (confidence >= 40) {  // Changed from 30
    return { 
      level: 'low', 
      label: 'Low Confidence', 
      color: 'text-blue-500', 
      emoji: '🔵' 
    };
  }
  return { 
    level: 'very_low', 
    label: 'Very Low Confidence', 
    color: 'text-gray-500', 
    emoji: '⚪' 
  };
}

/**
 * Filter violations by minimum confidence threshold
 */
export function filterByConfidence<T extends MockViolation | DatabaseViolation>(
  violations: T[],
  minConfidence: number = 40  // Changed from 60
): Array<T & { confidence: number }> {
  return violations
    .map(v => ({
      ...v,
      confidence: calculateViolationConfidence(v)
    }))
    .filter(v => v.confidence >= minConfidence)
    .sort((a, b) => b.confidence - a.confidence); // Sort by confidence DESC
}

/**
 * Group violations by confidence level
 */
export function groupByConfidence<T extends MockViolation | DatabaseViolation>(
  violations: T[]
): {
  veryHigh: Array<T & { confidence: number }>;
  high: Array<T & { confidence: number }>;
  medium: Array<T & { confidence: number }>;
  low: Array<T & { confidence: number }>;
  veryLow: Array<T & { confidence: number }>;
} {
  const withConfidence = violations.map(v => ({
    ...v,
    confidence: calculateViolationConfidence(v)
  }));

  return {
    veryHigh: withConfidence.filter(v => v.confidence >= 90).sort((a, b) => b.confidence - a.confidence),
    high: withConfidence.filter(v => v.confidence >= 70 && v.confidence < 90).sort((a, b) => b.confidence - a.confidence),
    medium: withConfidence.filter(v => v.confidence >= 50 && v.confidence < 70).sort((a, b) => b.confidence - a.confidence),
    low: withConfidence.filter(v => v.confidence >= 30 && v.confidence < 50).sort((a, b) => b.confidence - a.confidence),
    veryLow: withConfidence.filter(v => v.confidence < 30).sort((a, b) => b.confidence - a.confidence),
  };
}
